import api from "@/lib/api";
import { cacheEtat, isNetworkError } from "@/lib/offline";

// Photos prises hors-ligne : stockées dans IndexedDB, envoyées au retour du réseau
const DB = "madlo-photos", STORE = "photos";
const urls = new Map();
const listeners = new Set();
let flushing = false;

function openDb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id" });
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => res(req?.result);
    t.onerror = () => rej(t.error);
  });
}

export async function storeLocalPhoto(etatId, obsNumero, file) {
  const id = `local-${crypto.randomUUID()}`;
  await tx("readwrite", (s) => s.put({ id, etatId, obsNumero, blob: file, at: Date.now() }));
  urls.set(id, URL.createObjectURL(file));
  listeners.forEach((fn) => fn());
  return { id, local: true };
}

export function localPhotoUrl(id) { return urls.get(id) || ""; }
export function onLocalPhotosChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }

export async function preloadLocalPhotos() {
  try {
    const all = await tx("readonly", (s) => s.getAll());
    for (const p of all || []) if (!urls.has(p.id)) urls.set(p.id, URL.createObjectURL(p.blob));
    listeners.forEach((fn) => fn());
    return (all || []).length;
  } catch { return 0; }
}

export async function removeLocalPhoto(id) {
  await tx("readwrite", (s) => s.delete(id)).catch(() => {});
  const u = urls.get(id); if (u) URL.revokeObjectURL(u); urls.delete(id);
}

export async function pendingLocalPhotos() {
  try { return (await tx("readonly", (s) => s.getAll())) || []; } catch { return []; }
}

// Envoie les photos en attente puis remplace la référence locale dans le constat (PUT)
export async function flushLocalPhotos() {
  if (flushing || !navigator.onLine) return 0;
  flushing = true;
  let sent = 0;
  try {
    const all = await pendingLocalPhotos();
    const byEtat = all.reduce((m, p) => ((m[p.etatId] ||= []).push(p), m), {});
    for (const [etatId, photos] of Object.entries(byEtat)) {
      let etat;
      try { etat = (await api.get(`/etats/${etatId}`)).data; } catch (e) { if (isNetworkError(e)) break; continue; }
      let changed = false;
      for (const p of photos) {
        try {
          const fd = new FormData(); fd.append("file", p.blob, "photo.jpg");
          const { data } = await api.post(`/etats/${etatId}/photos`, fd);
          etat.observations = (etat.observations || []).map((o) => ({
            ...o, photos: (o.photos || []).map((ph) => ph.id === p.id ? { id: data.id, storage_path: data.storage_path, url: data.url } : ph),
          }));
          await removeLocalPhoto(p.id); changed = true; sent++;
        } catch (e) { if (isNetworkError(e)) { flushing = false; return sent; } await removeLocalPhoto(p.id); }
      }
      if (changed) {
        const { data } = await api.put(`/etats/${etatId}`, { observations: etat.observations });
        cacheEtat(data);
        window.dispatchEvent(new CustomEvent("madlo:etat-updated", { detail: data }));
      }
    }
  } finally { flushing = false; listeners.forEach((fn) => fn()); }
  return sent;
}
